import {useEffect, useState} from 'react';
import type {GroupCompanies} from '../../../core/entities/companyDetail/schema';
import type {GetCompanyInfoResponse} from '../../../core/repositories/companiesRepository';
import {
  type GetCompanyDetailResponse,
  getCompanyDetail,
  useGetCompanyPageColumnPosts,
  useGetGroupCompanies,
  useGetNativeAdPosts,
  useGetRecruitments,
} from '../../../core/repositories/companyDetailRepository';
import {useGetCompanyList} from '../../../core/repositories/companyRepository';

type Props = {
  companyDetail: GetCompanyDetailResponse;
  companyInfo: GetCompanyInfoResponse;
  year: string;
};

export const useHandleCompanyDetail = ({
  companyDetail,
  companyInfo,
  year,
}: Props) => {
  const [groupCompanies, setGroupCompanies] = useState<GroupCompanies>([]);

  const {data: nativeAdsData} = useGetNativeAdPosts({
    companyId: String(companyInfo.id),
  });
  const {data: companyPageColumnPostsData} = useGetCompanyPageColumnPosts({
    companyId: String(companyInfo.id),
  });
  const {data: recruitmentsData} = useGetRecruitments({
    companyId: String(companyInfo.id),
  });
  const {data: sameIndustryCompaniesData} = useGetCompanyList({
    queryParams: {
      industryIDs: [],
      industryCategoryIDs: [companyDetail.metaData?.industryCategoryID ?? 0],
      limit: 10,
      page: 1,
      cacheID: '',
    },
  });
  const {data: groupCompaniesData} = useGetGroupCompanies({
    companyId: String(companyInfo.id),
  });

  const nativeAds = nativeAdsData?.ok ? nativeAdsData.value : {posts: []};
  const companyPageColumnPosts = companyPageColumnPostsData?.ok
    ? companyPageColumnPostsData.value
    : {posts: []};
  const recruitments = recruitmentsData?.ok
    ? recruitmentsData.value
    : {recruitments: []};
  const sameIndustryCompanies = sameIndustryCompaniesData?.ok
    ? sameIndustryCompaniesData.value.companies
    : [];

  const groupCompanyIds = groupCompaniesData?.ok
    ? groupCompaniesData.value.map((company) => company.id.toString())
    : [];

  useEffect(() => {
    const fetchGroupCompanyDetails = async () => {
      if (groupCompanyIds.length > 0) {
        const groupCompanyDetails = await Promise.all(
          groupCompanyIds.map(async (companyId) => {
            const result = await getCompanyDetail({
              companyId,
              year,
            });
            return result.ok ? result.value : null;
          }),
        );

        const filteredGroupCompanyDetails = groupCompanyDetails.filter(
          (company): company is GetCompanyDetailResponse => company !== null,
        );

        setGroupCompanies(convertGroupCompanies(filteredGroupCompanyDetails));
      }
    };

    void fetchGroupCompanyDetails();
  }, [groupCompanyIds]);

  return {
    nativeAds,
    companyPageColumnPosts,
    recruitments,
    sameIndustryCompanies,
    groupCompanies,
  };
};

const convertGroupCompanies = (
  groupCompanies: GetCompanyDetailResponse[],
): GroupCompanies => {
  return groupCompanies.map((company) => ({
    id: company.metaData?.companyID ?? 0,
    name: company.metaData?.companyName ?? '',
    industry: company.metaData?.industryName ?? '',
    description: company.introduction?.description ?? '',
    hasReports: company.metaData?.hasReports ?? false,
  }));
};
